<script lang="ts" setup>
/**
 * リプライ投稿 > リプライ投稿内容。
 */

import { reactive, inject, toRefs, defineProps, PropType, watch } from "vue";
import AppConst from "@/modules/const";
import { ApiValidationErrorViewModel } from "@/viewModels/apiErrorViewModel";
import CustomTextarea from "@/components/common/form/CustomTextarea.vue";
import CustomLabel from "@/components/communityTalk/CustomLabel.vue";
import { ReplyPostStore, isValidReplyPostStoreKey } from "@/store/communityTalk/replyPostStore";

const props = defineProps({
	/**
	 * ReplyPostStoreのInject用キー。
	 */
	replyPostStoreKey: {
		type: String,
		required: true,
	},
	/**
	 * 入力欄を無効化するかどうか。
	 */
	disabled: {
		type: Boolean,
		required: false,
		default: false,
	},
	/**
	 * 親コンポーネントからのAPIエラーオブジェクト。
	 */
	apiValidationErrorViewModel: {
		type: Object as PropType<ApiValidationErrorViewModel | null>,
		required: true,
		default: null,
	},
	/**
	 * 入力必須エラーを表示するかどうか。
	 */
	showRequiredError: {
		type: Boolean,
		required: false,
		default: false,
	},
});

// Storeの初期化処理
if (!isValidReplyPostStoreKey(props.replyPostStoreKey)) {
	// なんらかの原因でInjectKeyにエラーが発生していた場合は強制終了
	throw new Error(`InjectKeyにエラーが発生しました。${props.replyPostStoreKey.toString()}`);
}
const replyPostStore = inject(props.replyPostStoreKey) as ReplyPostStore;

interface State {
	/**
	 * リプライ投稿: 投稿内容。
	 */
	contents: string;
	/**
	 * 入力値が最大文字長を超えているかどうか。
	 */
	isOverContentsMaxLength: boolean;
}

// reactiveな変数宣言
const state = reactive<State>({
	contents: replyPostStore.getContents(),
	isOverContentsMaxLength: false,
});
const { contents, isOverContentsMaxLength } = toRefs(state);

/**
 * リプライ内容の投稿最大文字長。
 */
const replyContentsMaxLength = AppConst.REPLY_CONTENTS_MAX_LENGTH;

// 入力欄変更時、Storeへ投稿者プロフィール情報を反映
const onChange = (): void => {
	replyPostStore.setContents(state.contents);

	if (state.contents.length > replyContentsMaxLength) {
		state.isOverContentsMaxLength = true;
	} else {
		state.isOverContentsMaxLength = false;
	}
};

/**
 * 投稿内容が他コンポーネント等から変更された場合、変更を反映する。
 */
watch(
	() => replyPostStore.getContents(),
	(contents) => {
		state.contents = contents;
	}
);
</script>

<template>
	<custom-label class="revico-community-talk-reply-post--contents--label" text="内容(必須)" :disabled="disabled" />
	<custom-textarea
		v-model="contents"
		@update:modelValue="onChange()"
		name="reply.Contents"
		customClass="revico-community-talk-reply-post--contents"
		:api-validation-error-view-model="apiValidationErrorViewModel"
		:maxlength="10000"
		:disabled="disabled"
		placeholder="返信をお書きください"
		:rows="5"
		:show-required-error="showRequiredError"
		label="内容"
	/>
	<span v-if="isOverContentsMaxLength" class="revico-community-talk-reply-post--contents-error-message">{{ replyContentsMaxLength }}文字以下で投稿してください。</span>
</template>

<style lang="scss">
.revico-widget .revico-community-talk {
	&-reply-post--contents {
		&-error-message {
			display: inline-block;
			margin-top: 4px;
			color: var(--base-error-message-font-color);
			font-size: 0.9em;
		}
	}
}
</style>
